import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import {
  aggregateDayOfWeek,
  aggregateDayOfMonth,
  getChronologicallyIndexedTrades,
  computeMaxLosingStreakDetails,
  computeOvertradingMeta,
  aggregateMidMonthSegments,
  generateTimeBasedInsightsDetailed,
  MIN_TRADES_FOR_LOW_SAMPLE_WARNING
} from '../utils/timeBasedPerformance';
import './TimeBasedPerformance.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function dowRowBg(totalPnl, minPnl, maxPnl) {
  if (maxPnl === minPnl && minPnl === 0) return 'rgba(248, 249, 250, 1)';
  const span = Math.max(maxPnl - minPnl, 1e-6);
  const t = (totalPnl - minPnl) / span;
  if (totalPnl >= 0) {
    const g = 0.92 + t * 0.06;
    return `rgba(220, 255, 235, ${g})`;
  }
  const r = 0.92 + (1 - t) * 0.06;
  return `rgba(255, 226, 230, ${r})`;
}

function DayOfWeekTable({ dayRows }) {
  const { minPnl, maxPnl, bestLabels, worstLabels, summaryLine, distinctBestWorst } = useMemo(() => {
    const active = dayRows.filter(r => r.totalTrades > 0);
    const pnls = active.map(r => r.totalPnl);
    const minPnl = pnls.length ? Math.min(...pnls) : 0;
    const maxPnl = pnls.length ? Math.max(...pnls) : 0;
    const bestVal = maxPnl;
    const worstVal = minPnl;
    const bestLabels = active.filter(r => r.totalPnl === bestVal).map(r => r.label);
    const worstLabels = active.filter(r => r.totalPnl === worstVal).map(r => r.label);
    const distinctBestWorst = bestVal !== worstVal;

    let summaryLine = '';
    if (active.length === 0) {
      summaryLine = 'No weekday trades to summarize.';
    } else {
      // Match the table highlights: best / worst by total P&L among all weekdays that have trades.
      const overallBest = active.reduce((a, b) => (a.totalPnl >= b.totalPnl ? a : b));
      const overallWorst = active.reduce((a, b) => (a.totalPnl <= b.totalPnl ? a : b));
      const fmt = row => {
        const s = row.totalPnl >= 0 ? '+' : '';
        return `${row.label} (${s}₹${row.totalPnl.toFixed(0)}, ${row.winRate.toFixed(0)}% win rate)`;
      };
      summaryLine = `Best day: ${fmt(overallBest)}`;
      if (active.length >= 2 && overallWorst.label !== overallBest.label) {
        summaryLine += ` · Worst: ${fmt(overallWorst)}`;
      }
    }

    return { minPnl, maxPnl, bestLabels, worstLabels, summaryLine, distinctBestWorst };
  }, [dayRows]);

  return (
    <div className="tbp-dow-wrap">
      <div className="table-container tbp-table-scroll">
        <table className="trades-table tbp-dow-table">
          <thead>
            <tr>
              <th>Day</th>
              <th className="tbp-num">Total trades</th>
              <th className="tbp-num">Total P&amp;L</th>
              <th className="tbp-num">Win rate</th>
            </tr>
          </thead>
          <tbody>
            {dayRows.map(row => {
              const isBest = row.totalTrades > 0 && bestLabels.includes(row.label);
              const isWorst = row.totalTrades > 0 && worstLabels.includes(row.label);
              let rowClass = '';
              if (isBest && isWorst && !distinctBestWorst) rowClass = 'tbp-dow-best';
              else {
                if (isBest) rowClass += ' tbp-dow-best';
                if (isWorst && distinctBestWorst) rowClass += ' tbp-dow-worst';
              }
              return (
                <tr
                  key={row.label}
                  className={rowClass.trim()}
                  style={{ background: dowRowBg(row.totalPnl, minPnl, maxPnl) }}
                  title={
                    isBest && (!isWorst || !distinctBestWorst)
                      ? 'Highest total P&L (weekday)'
                      : isWorst && distinctBestWorst
                        ? 'Lowest total P&L (weekday)'
                        : undefined
                  }
                >
                  <td className="tbp-day-cell">{row.label}</td>
                  <td className="tbp-num">{row.totalTrades}</td>
                  <td className={`tbp-num ${row.totalTrades === 0 ? '' : row.totalPnl >= 0 ? 'profit' : 'loss'}`}>
                    {row.totalTrades === 0 ? '—' : `₹${row.totalPnl.toFixed(0)}`}
                  </td>
                  <td className="tbp-num">
                    {row.totalTrades === 0 ? '—' : `${row.winRate.toFixed(1)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="tbp-dow-summary">{summaryLine}</p>
    </div>
  );
}

function domCellWinRate(c) {
  const denom = c.wins + c.losses + c.breakEven;
  return denom > 0 ? ((c.wins / denom) * 100).toFixed(1) : '0.0';
}

function domCellsFromMap(domMap) {
  const list = [];
  for (let d = 1; d <= 31; d++) {
    const b = domMap.get(d);
    list.push({
      dom: d,
      pnl: b.pnl,
      trades: b.trades,
      wins: b.wins,
      losses: b.losses,
      breakEven: b.breakEven
    });
  }
  return list;
}

function buildDayOfMonthBarChartData(cells) {
  const labels = cells.map(c => String(c.dom));
  const data = cells.map(c => (c.trades === 0 ? null : c.pnl));
  const bg = cells.map(c => {
    if (c.trades === 0) return 'rgba(0, 0, 0, 0)';
    if (c.pnl > 0) return 'rgba(0, 155, 90, 0.9)';
    if (c.pnl < 0) return 'rgba(215, 58, 58, 0.92)';
    return 'rgba(115, 115, 115, 0.8)';
  });
  return {
    labels,
    datasets: [
      {
        label: 'P&L (₹)',
        data,
        backgroundColor: bg,
        borderRadius: 5,
        borderSkipped: false,
        barPercentage: 0.9,
        categoryPercentage: 0.92,
        maxBarThickness: 28
      }
    ]
  };
}

/** @param {{ xTitle?: string }} [opts] */
function buildDayOfMonthBarOptions(cells, opts = {}) {
  const xTitle = opts.xTitle ?? 'Calendar day of month';
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: items => {
            const i = items[0].dataIndex;
            return `Day ${cells[i].dom} of month (all months combined)`;
          },
          afterTitle: () => 'Same calendar date across your history (e.g. every 12th).',
          label: item => {
            const c = cells[item.dataIndex];
            if (c.trades === 0) return ' No completed trades on this day-of-month';
            const wr = domCellWinRate(c);
            return [
              ` Round-trips: ${c.trades}`,
              ` P&L: ₹${c.pnl.toFixed(2)}`,
              ` Win rate: ${wr}%`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: xTitle,
          font: { size: 12, weight: '600' }
        },
        grid: { display: false },
        ticks: { font: { size: 11, weight: '600' }, maxRotation: 0 }
      },
      y: {
        grid: { color: 'rgba(0, 0, 0, 0.07)' },
        ticks: {
          callback: v => `₹${v}`
        },
        beginAtZero: false
      }
    }
  };
}

/** Vertical bars for P&L by calendar day-of-month (1–31), all months combined */
function DayOfMonthPnLBars({ domMap }) {
  const cells = useMemo(() => domCellsFromMap(domMap), [domMap]);
  const chartData = useMemo(() => buildDayOfMonthBarChartData(cells), [cells]);
  const options = useMemo(() => buildDayOfMonthBarOptions(cells), [cells]);

  return (
    <div className="tbp-dom-bars-wrap">
      <p className="tbp-dom-bars-legend">
        <span className="tbp-legend-profit">Profit</span>
        <span className="tbp-dom-bars-sep">·</span>
        <span className="tbp-legend-loss">Loss</span>
        <span className="tbp-dom-bars-sep">·</span>
        <span className="tbp-dom-bars-muted">No bar = no trades on that day-of-month</span>
      </p>
      <div className="tbp-dom-bars-chart">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

function InsightsPanel({ items }) {
  const formatPnl = n => `${n >= 0 ? '+' : '-'}₹${Math.abs(Number(n) || 0).toFixed(2)}`;

  const renderProof = (proof) => {
    if (!proof || !proof.kind) return <p>No proof data available.</p>;
    const p = proof.payload || {};

    if (proof.kind === 'low_sample') {
      return (
        <>
          <p>Completed trades: <strong>{p.completedTrades}</strong> (suggested minimum: {p.minTradesRecommended})</p>
          {p.dateRange && <p>Date range: {p.dateRange.from} to {p.dateRange.to}</p>}
        </>
      );
    }

    if (proof.kind === 'weekday') {
      if (!p.rows || p.rows.length === 0) return <p>No weekday rows available.</p>;
      return (
        <>
          {p.selected && (
            <p>
              Selected day: <strong>{p.selected.label}</strong> | Trades: {p.selected.totalTrades} | P&amp;L: {formatPnl(p.selected.totalPnl)} | Win rate: {p.selected.winRate.toFixed(1)}%
            </p>
          )}
          <div className="tbp-proof-table-wrap">
            <table className="tbp-proof-table">
              <thead>
                <tr><th>Day</th><th>Trades</th><th>P&amp;L</th><th>Win %</th></tr>
              </thead>
              <tbody>
                {p.rows.map(r => (
                  <tr key={r.label}>
                    <td>{r.label}</td><td>{r.totalTrades}</td><td>{formatPnl(r.totalPnl)}</td><td>{r.winRate.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }

    if (proof.kind === 'losing_streak') {
      return (
        <>
          <p>Window indices in sorted completed trades: [{p.startIndex}..{p.endIndex}]</p>
          <div className="tbp-proof-table-wrap">
            <table className="tbp-proof-table">
              <thead>
                <tr><th>Idx</th><th>Date</th><th>Symbol</th><th>P&amp;L</th></tr>
              </thead>
              <tbody>
                {(p.tradeRefs || []).map(ref => (
                  <tr key={`${ref.localIndex}-${ref.tradeDate}-${ref.symbol}`}>
                    <td>{ref.localIndex}</td><td>{ref.tradeDate || '-'}</td><td>{ref.symbol}</td><td>{formatPnl(ref.pnl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }

    if (proof.kind === 'overtrading_days') {
      return (
        <>
          <p>Threshold: more than {p.threshold} completed trades/day. Overtrading days: {p.overCount}</p>
          <div className="tbp-proof-table-wrap">
            <table className="tbp-proof-table">
              <thead>
                <tr><th>Date</th><th>Trades</th><th>P&amp;L</th></tr>
              </thead>
              <tbody>
                {(p.overDays || []).map(d => (
                  <tr key={d.date}><td>{d.date}</td><td>{d.count}</td><td>{formatPnl(d.pnl)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }

    if (proof.kind === 'overtrading_impact') {
      return (
        <>
          <p>Avg P&amp;L on high-trade days: <strong>{formatPnl(p.avgOver)}</strong> ({p.overDaysCount} days)</p>
          <p>Avg P&amp;L on normal days: <strong>{formatPnl(p.avgNormal)}</strong> ({p.normalDaysCount} days)</p>
        </>
      );
    }

    if (proof.kind === 'midmonth') {
      return (
        <div className="tbp-proof-table-wrap">
          <table className="tbp-proof-table">
            <thead>
              <tr><th>Segment</th><th>Range</th><th>Trades</th><th>P&amp;L</th><th>Win %</th></tr>
            </thead>
            <tbody>
              {(p.segments || []).map(s => (
                <tr key={s.key} className={p.selected && s.key === p.selected.key ? 'tbp-proof-selected' : ''}>
                  <td>{s.key}</td><td>{s.range[0]}-{s.range[1]}</td><td>{s.totalTrades}</td><td>{formatPnl(s.totalPnl)}</td><td>{s.winRate.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <p>No additional proof details.</p>;
  };

  return (
    <ul className="tbp-insights-list">
      {items.map((item, i) => (
        <li key={item.id || i} className={`tbp-insight-card ${i % 2 === 0 ? 'tbp-insight-a' : 'tbp-insight-b'}`}>
          <div className="tbp-insight-main">
            <strong className="tbp-insight-title">{item.title}</strong>
            <div className="tbp-insight-summary">{item.summary}</div>
          </div>
          <div className="tbp-insight-proof-popover">{renderProof(item.proof)}</div>
        </li>
      ))}
    </ul>
  );
}

function TimeCharts({ dayRows }) {
  const barData = useMemo(
    () => ({
      labels: dayRows.map(r => r.label.slice(0, 3)),
      datasets: [
        {
          label: 'Total P&L (₹)',
          data: dayRows.map(r => (r.totalTrades === 0 ? null : r.totalPnl)),
          backgroundColor: dayRows.map(r =>
            r.totalTrades === 0 ? 'rgba(200,200,200,0.2)' : r.totalPnl >= 0 ? 'rgba(0, 180, 100, 0.65)' : 'rgba(230, 70, 70, 0.65)'
          ),
          borderRadius: 4,
          borderSkipped: false
        }
      ]
    }),
    [dayRows]
  );

  const commonOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        filter: item => item.raw != null,
        callbacks: {
          label: ctx => {
            const v = ctx.parsed.y;
            if (v == null) return '';
            return ` ₹${Number(v).toFixed(2)}`;
          }
        }
      }
    }
  };

  return (
    <div className="tbp-charts-row tbp-charts-row-single">
      <div className="tbp-chart-box">
        <h4 className="tbp-chart-title">P&amp;L by weekday</h4>
        <p className="tbp-chart-sub">Day-of-month P&amp;L is shown as bars in the section above.</p>
        <div className="tbp-chart-inner">
          <Bar
            data={barData}
            options={{
              ...commonOpts,
              scales: {
                x: { grid: { display: false }, ticks: { maxRotation: 0 } },
                y: {
                  grid: { color: 'rgba(0,0,0,0.06)' },
                  ticks: { callback: v => '₹' + v }
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

function TimeBasedPerformance({ completedTrades }) {
  const trades = completedTrades || [];

  const dayRows = useMemo(() => aggregateDayOfWeek(trades), [trades]);
  const domMap = useMemo(() => aggregateDayOfMonth(trades), [trades]);

  const sorted = useMemo(() => getChronologicallyIndexedTrades(trades), [trades]);
  const losingStreakDetails = useMemo(() => computeMaxLosingStreakDetails(sorted), [sorted]);
  const overMeta = useMemo(() => computeOvertradingMeta(trades), [trades]);
  const midSeg = useMemo(() => aggregateMidMonthSegments(trades), [trades]);
  const insights = useMemo(
    () => generateTimeBasedInsightsDetailed(trades, dayRows, losingStreakDetails, overMeta, midSeg),
    [trades, dayRows, losingStreakDetails, overMeta, midSeg]
  );

  const hasTrades = trades.length > 0;
  const lowSample = trades.length > 0 && trades.length < MIN_TRADES_FOR_LOW_SAMPLE_WARNING;

  if (!hasTrades) {
    return (
      <section className="time-based-performance tbp-section">
        <div className="card tbp-block">
          <h3 className="tbp-heading">Time-Based Performance</h3>
          <p className="tbp-muted">No completed trades yet — upload or add trades to see behavioral patterns by time.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="time-based-performance tbp-section">
      <div className="card tbp-block">
        <h3 className="tbp-heading">Time-Based Performance</h3>
        <p className="tbp-block-desc tbp-lead">
          Weekday discipline, day-of-month clustering, streaks, and overtrading — from{' '}
          <strong>completed</strong> round-trips only.
        </p>
        {lowSample && (
          <p className="tbp-banner">
            Low sample size ({trades.length} completed trades). Use this section for direction only, not conclusions.
          </p>
        )}
        <h4 className="tbp-subblock-title">Day-of-week</h4>
        <p className="tbp-block-desc">Monday–Friday only; weekend trades are excluded from this table.</p>
        <DayOfWeekTable dayRows={dayRows} />
      </div>

      <div className="card tbp-block">
        <h3 className="tbp-block-title">P&amp;L by date-of-month (1–31)</h3>
        <p className="tbp-block-desc">
          Bar height is total P&amp;L for that calendar day-of-month (e.g. all 3rds, all 17ths), across every month in your data.
        </p>
        <DayOfMonthPnLBars domMap={domMap} />
      </div>

      <div className="card tbp-block">
        <h3 className="tbp-block-title">Auto-generated insights</h3>
        <p className="tbp-block-desc">Rules stay conservative so sparse data does not read like certainty.</p>
        <InsightsPanel items={insights} />
      </div>

      <div className="card tbp-block">
        <h3 className="tbp-block-title">Charts</h3>
        <TimeCharts dayRows={dayRows} />
      </div>
    </section>
  );
}

export default TimeBasedPerformance;
